import { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faChevronDown,
  faBell,
} from "@fortawesome/free-solid-svg-icons";
import "./NotificationAnimations.css";
import "./ResponsiveUtils.css";
import { useNotifications } from "../../hooks/notifications/useNotifications";
import { useNotificationQueryContext } from "../../context/NotificationQueryContext";
import NotificationItem from "./NotificationItem";
import NotificationEmptyState from "./NotificationEmptyState";
import BulkActions from "./BulkActions";
import SelectAllCheckbox from "./SelectAllCheckbox";
import NotificationFilters from "./NotificationFilters";
import NotificationPagination from "./NotificationPagination";
import VirtualizedNotificationList from "./VirtualizedNotificationList";
import NotificationSkeleton from "./NotificationSkeleton";
import useInfiniteScroll from "../../hooks/notifications/useInfiniteScroll";
import usePullToRefresh from "../../hooks/notifications/usePullToRefresh";
import useAccessibility from "../../hooks/notifications/useAccessibility";

/**
 * NotificationList Component
 *
 * Full-featured notification list with bulk operations, filtering, and pagination
 * Supports virtual scrolling for performance with large lists
 */
const NotificationList = ({
  showBulkActions = true,
  showFilters = true,
  showRefresh = true,
  showPagination = true,
  infiniteScroll = false,
  virtualScrolling = false,
  onNotificationClick,
  className = "",
}) => {
  const {
    selectedNotifications,
    clearSelection,
    queryParams,
    filters,
    clearFilters,
  } = useNotificationQueryContext();

  const {
    data: notificationsData,
    isLoading,
    error,
    refetch,
  } = useNotifications(queryParams);

  const notifications = notificationsData?.data?.notifications || [];
  const pagination = notificationsData?.data?.pagination || {};

  const [isRefreshing, setIsRefreshing] = useState(false);
  const scrollContainerRef = useRef(null);

  // Use accessibility hook
  const { getAccessibleProps, announceNotificationChange } = useAccessibility({
    announceChanges: true,
  });

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    announceNotificationChange("loading");
    try {
      await refetch();
      clearSelection();
      announceNotificationChange("unread-count-changed", {
        count: notifications.filter((n) => !n.read).length,
      });
    } catch (error) {
      announceNotificationChange("error", {
        message: "Failed to refresh notifications",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Pull-to-refresh functionality
  const {
    handleTouchStart: handlePullTouchStart,
    handleTouchMove: handlePullTouchMove,
    handleTouchEnd: handlePullTouchEnd,
    handleTouchCancel: handlePullTouchCancel,
    isPulling,
    isRefreshing: isPullRefreshing,
    getPullIndicatorStyle,
    getContainerStyle,
    shouldShowIndicator,
    pullProgress,
  } = usePullToRefresh({
    onRefresh: handleRefresh,
    threshold: 80,
    enabled: true,
    containerRef: scrollContainerRef,
  });

  // Set up infinite scroll if enabled
  const { isLoading: isLoadingMore } = useInfiniteScroll({
    threshold: 200,
    enabled: infiniteScroll && !virtualScrolling,
    containerRef: scrollContainerRef,
  });

  // React Query automatically handles data fetching

  // Handle load more (for pagination)
  const handleLoadMore = async () => {
    if (pagination.hasNext && !isLoading) {
      try {
        await refetch();
      } catch (error) {
        // Handle error silently
      }
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
  };

  return (
    <div
      className={`notification-list ${className}`}
      {...getAccessibleProps("notification-list", {
        title: "Notifications",
        loading: isLoading,
      })}
    >
      {/* Modern Header with controls */}
      <div className="notification-list-header bg-gradient-to-r from-white to-light-grey border-b border-gray-100 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-deep-red to-bright-red rounded-xl flex items-center justify-center">
              <FontAwesomeIcon icon={faBell} className="text-white text-sm" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-dark-grey">
              Your Notifications
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {showRefresh && (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing || isLoading}
                className="btn btn--soft-primary
                  /* Touch-friendly button */
                  min-h-[40px] sm:min-h-0"
                title="Refresh notifications"
              >
                <FontAwesomeIcon
                  icon={faSync}
                  className={`${isRefreshing ? "animate-spin" : ""}`}
                />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            )}
          </div>
        </div>

        {/* Modern Filters */}
        {showFilters && (
          <div className="mb-4 sm:mb-6">
            <NotificationFilters />
          </div>
        )}

        {/* Modern Bulk selection and actions */}
        {showBulkActions && notifications.length > 0 && (
          <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
            <SelectAllCheckbox />

            {selectedNotifications.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-deep-red rounded-full"></div>
                <div className="text-sm font-medium text-medium-grey">
                  {selectedNotifications.length} of {notifications.length}{" "}
                  selected
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {showBulkActions && selectedNotifications.length > 0 && (
        <div className="p-3 sm:p-4 bg-gray-50 border-b border-gray-200">
          <BulkActions />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <div className="flex items-center">
            <div className="text-sm text-red-700">
              Failed to load notifications: {error.message || "Unknown error"}
            </div>
            <button
              onClick={handleRefresh}
              className="ml-3 text-sm text-red-600 hover:text-red-800 focus:outline-none focus:underline"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Pull-to-refresh indicator */}
      {shouldShowIndicator && (
        <div
          className="pull-to-refresh-indicator flex items-center justify-center py-4 bg-gray-50 border-b border-gray-200"
          style={getPullIndicatorStyle()}
        >
          <div className="flex items-center space-x-2 text-gray-600">
            {isPullRefreshing ? (
              <>
                <FontAwesomeIcon icon={faSync} className="animate-spin" />
                <span className="text-sm">Refreshing...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`transform transition-transform ${
                    pullProgress >= 1 ? "rotate-180" : ""
                  }`}
                />
                <span className="text-sm">
                  {pullProgress >= 1 ? "Release to refresh" : "Pull to refresh"}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Notification List Content */}
      <div
        ref={scrollContainerRef}
        className={`notification-list-content ${
          infiniteScroll ? "overflow-y-auto" : ""
        } ${isPulling ? "touch-none" : ""}`}
        style={{
          ...(infiniteScroll ? { maxHeight: "600px" } : {}),
          ...getContainerStyle(),
        }}
        onTouchStart={handlePullTouchStart}
        onTouchMove={handlePullTouchMove}
        onTouchEnd={handlePullTouchEnd}
        onTouchCancel={handlePullTouchCancel}
      >
        {isLoading && notifications.length === 0 ? (
          // Initial loading state with skeleton
          <NotificationSkeleton count={5} />
        ) : notifications.length === 0 ? (
          // Modern Empty state - show different variant based on filters
          <NotificationEmptyState
            variant={
              filters.type !== null || filters.read !== null
                ? "no-results"
                : "empty"
            }
            hasActiveFilters={filters.type !== null || filters.read !== null}
            onClearFilters={clearFilters}
            className="py-16"
          />
        ) : virtualScrolling ? (
          // Virtual scrolling mode
          <VirtualizedNotificationList
            height={600}
            itemHeight={80}
            showCheckbox={showBulkActions}
            onNotificationClick={handleNotificationClick}
          />
        ) : (
          // Regular notification items
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                showCheckbox={showBulkActions}
                isSelected={selectedNotifications.includes(notification._id)}
                onClose={() => handleNotificationClick(notification)}
              />
            ))}
          </div>
        )}

        {/* Load More Button (for manual pagination) */}
        {!infiniteScroll &&
          !virtualScrolling &&
          pagination.hasNext &&
          !isLoading && (
            <div className="p-4 text-center border-t border-gray-100">
              <button
                onClick={handleLoadMore}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-300 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Load more notifications
              </button>
            </div>
          )}

        {/* Loading more indicator (for infinite scroll) */}
        {(infiniteScroll || virtualScrolling) &&
          (isLoadingMore || isLoading) &&
          notifications.length > 0 && (
            <div className="p-4 text-center border-t border-gray-100">
              <div className="inline-flex items-center text-sm text-gray-500">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mr-2" />
                Loading more notifications...
              </div>
            </div>
          )}
      </div>

      {/* Pagination Controls */}
      {showPagination &&
        !infiniteScroll &&
        !virtualScrolling &&
        notifications.length > 0 && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <NotificationPagination />
          </div>
        )}
    </div>
  );
};

export default NotificationList;
